import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Modal.css'; // 既存 of Modal.cssをインポートしてスタイルを再利用
import AdBanner from './AdBanner';

interface FullscreenAdProps {
  /** カウントダウン秒数 */
  countdownSeconds: number;
  /** 広告を閉じたときのコールバック */
  onClose: () => void;
}

type Phase = 'notice' | 'ad';

const FullscreenAd = ({ countdownSeconds, onClose }: FullscreenAdProps) => {
  const [phase, setPhase] = useState<Phase>('notice');
  const [countdown, setCountdown] = useState(countdownSeconds);
  const [canClose, setCanClose] = useState(false);
  const [adLoaded, setAdLoaded] = useState(true); // 広告表示用の状態

  // フェーズ1（案内） → フェーズ2（広告） への移行
  useEffect(() => {
    const timer = setTimeout(() => {
      setPhase('ad');
    }, 1500);
    return () => clearTimeout(timer);
  }, []);
  

  // カウントダウン処理
  useEffect(() => {
    if (phase !== 'ad') return;
    if (countdown <= 0) {
      setCanClose(true);
      return;
    }
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setCanClose(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, countdown]);

  const handleClose = useCallback(() => {
    if (canClose) {
      onClose();
    }
  }, [canClose, onClose]);

  // 広告が読み込まれたことを検知するコールバック
  const handleAdLoad = useCallback(() => {
    setAdLoaded(true);
  }, []);

  return (
    <div className="gallery-modal-overlay" style={{ zIndex: 99999 }}>
      <div 
        className="gallery-modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '1600px',
          width: '95%',
          height: '90vh',
          minHeight: '450px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px 30px',
          boxSizing: 'border-box',
          border: '1px solid #333',
          background: '#1a1a1a',
          fontFamily: '"Zen Maru Gothic", sans-serif',
          textAlign: 'center',
        }}
      >
        {/* カウントダウン完了時のみ表示される閉じるボタン（右上） */}
        {canClose && (
          <button 
            className="gallery-modal-close" 
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '15px',
              right: '15px',
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '24px',
              cursor: 'pointer',
              opacity: 0.7,
            }}
          >
            &times;
          </button>
        )}

        <AnimatePresence mode="wait">
          {phase === 'notice' ? (
            <motion.div
              key="notice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '15px',
              }}
            >
              <h3>
                ホームページ運営のため<br />ご協力お願いします。
              </h3>
              <p style={{
                fontSize: '0.70rem',
                color: '#888',
                margin: 0,
              }}>
                まもなく広告が表示されます。
              </p>
              {/* シンプルな読み込み中アニメーション */}
              <div style={{
                width: '24px',
                height: '24px',
                border: '2px solid rgba(255,255,255,0.1)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginTop: '10px',
              }} />
              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </motion.div>
          ) : (
            <motion.div
              key="ad"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '20px',
              }}
            >
              {/* カウントダウン表示 */}
              <div style={{
                fontSize: '0.85rem',
                color: '#aaa',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                {!canClose ? (
                  <>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(255,255,255,0.1)',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      fontWeight: 'bold',
                      color: 'white',
                    }}>
                      {countdown}
                    </span>
                    <span>秒後に閉じることができます</span>
                  </>
                ) : (
                  <span style={{ color: '#4caf50', fontWeight: 'bold' }}></span>
                )}
              </div>

              {/* 広告表示エリア */}
              <div style={{
                position: 'relative',
                width: '100%',
                height: '100%',
                flex: 1,
                minHeight: '250px',
                margin: '15px 0',
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: '8px',
                overflow: 'hidden',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                {/* 背後のプレースホルダー（広告未読込時のみ表示） */}
                {!adLoaded && (
                  <motion.div
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px',
                      pointerEvents: 'none',
                      zIndex: 0,
                    }}
                  >
                    <div style={{
                      fontSize: '0.7rem',
                      color: '#666',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}>
                      Sponsored
                    </div>
                    <div style={{
                      fontSize: '0.9rem',
                      color: '#ccc',
                      lineHeight: 1.5,
                    }}>
                      毒と花と6畳間<br />
                      【広告枠】
                    </div>
                  </motion.div>
                )}

                {/* 前面のアドセンス広告 */}
                <AdBanner 
                  slot="2523042972"
                  format="auto"
                  onAdLoad={handleAdLoad}
                  style={{ 
                    position: 'absolute',
                    inset: 0,
                    margin: 0,
                    background: '#1a1a1a',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: adLoaded ? 1 : -1, // 読み込まれたら前面に
                  }} 
                />
              </div>

              {/* 下部の閉じるボタン */}
              {canClose && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={handleClose}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: '#ff4d4f',
                    border: 'none',
                    borderRadius: '24px',
                    color: 'white',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    transition: 'background 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#ff7875';
                  }}
                  onMouseLeave={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#ff4d4f';
                  }}
                >
                  広告を閉じる
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FullscreenAd;